import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, MapPin, Clock, Briefcase, Heart, TrendingUp, Users,
  Coffee, GraduationCap, Laptop, Send, CheckCircle, Code, Database,
  Palette, Headphones, ChevronDown
} from 'lucide-react';
import SEO, { ORG_SCHEMA } from '../components/SEO';
import './Careers.css';

const PERKS = [
  { icon: <Heart size={22} />, title: 'Health Insurance', desc: 'Comprehensive medical coverage for you and your family.' },
  { icon: <TrendingUp size={22} />, title: 'Growth Path', desc: 'Clear career progression with mentorship from senior engineers.' },
  { icon: <Coffee size={22} />, title: 'Flexible Hours', desc: 'Hybrid work options with flexible start times.' },
  { icon: <GraduationCap size={22} />, title: 'Learning Budget', desc: 'Annual budget for courses, certifications and conferences.' },
  { icon: <Users size={22} />, title: 'Team Culture', desc: 'Small, close-knit teams where your work is genuinely seen.' },
  { icon: <Laptop size={22} />, title: 'Modern Tooling', desc: 'Work with the latest stacks — no legacy tech debt drag.' },
];

const DEPARTMENTS = ['All', 'Engineering', 'Design', 'Sales', 'Support'];

const OPENINGS = [
  {
    title: 'Senior Full-Stack Developer',
    dept: 'Engineering',
    icon: <Code size={20} />,
    location: 'Delhi (Hybrid)',
    type: 'Full-time',
    experience: '1-3 years',
    desc: 'Build and scale our core RTA and ERP platforms using React, C# .net core and MySQL. You will own features end-to-end, from architecture to deployment.',
    skills: ['React', 'C#', 'MySQL', 'REST APIs'],
  },
  {
    title: 'Database Engineer',
    dept: 'Engineering',
    icon: <Database size={20} />,
    location: 'Delhi (On-site)',
    type: 'Full-time',
    experience: '3-5 years',
    desc: 'Design and optimize database schemas handling millions of financial transactions. Own performance tuning, replication and backup strategy.',
    skills: ['MySQL', 'Redis', 'Query Optimization', 'AWS RDS'],
  },
  {
    title: 'Product Designer (UI/UX)',
    dept: 'Design',
    icon: <Palette size={20} />,
    location: 'Remote',
    type: 'Full-time',
    experience: '2-4 years',
    desc: 'Shape the experience of enterprise software used by schools, hotels and financial institutions. Turn complex workflows into intuitive interfaces.',
    skills: ['Figma', 'Design Systems', 'User Research', 'Prototyping'],
  },
  {
    title: 'Business Development Executive',
    dept: 'Sales',
    icon: <Briefcase size={20} />,
    location: 'Delhi (On-site)',
    type: 'Full-time',
    experience: '1-3 years',
    desc: 'Drive new client acquisition across finance, education and hospitality sectors. Build relationships and close deals for our product suite.',
    skills: ['B2B Sales', 'CRM', 'Communication', 'Negotiation'],
  },
  {
    title: 'Technical Support Engineer',
    dept: 'Support',
    icon: <Headphones size={20} />,
    location: 'Delhi (Hybrid)',
    type: 'Full-time',
    experience: '1-3 years',
    desc: 'Be the first point of contact for client issues across our product suite. Diagnose, troubleshoot, and escalate with clear documentation.',
    skills: ['MySQL', 'Troubleshooting', 'Client Communication', 'Ticketing Systems'],
  },
  {
    title: 'React Native Developer',
    dept: 'Engineering',
    icon: <Code size={20} />,
    location: 'Remote',
    type: 'Contract',
    experience: '2-4 years',
    desc: 'Build cross-platform mobile apps that extend our School ERP and Hotel Management products to parents, students and staff on the go.',
    skills: ['React Native', 'iOS', 'Android', 'Offline-first Apps'],
  },
];

const PROCESS_STEPS = [
  { n: '01', title: 'Apply', desc: 'Submit your application with resume and a short note on why you\'re excited about SHIVKMS.' },
  { n: '02', title: 'Screening Call', desc: 'A 20-minute call with our hiring team to understand your background and goals.' },
  { n: '03', title: 'Technical Round', desc: 'Role-specific assessment — a take-home task or live problem-solving session.' },
  { n: '04', title: 'Culture Fit', desc: 'Meet the team you\'ll work with directly. We care as much about fit as skill.' },
  { n: '05', title: 'Offer', desc: 'Clear, transparent offer with compensation, growth path and start date discussed upfront.' },
];

export default function Careers() {
  const [activeDept, setActiveDept] = useState('All');
  const [expandedJob, setExpandedJob] = useState(null);

  const filtered = activeDept === 'All' ? OPENINGS : OPENINGS.filter(o => o.dept === activeDept);

  const careersSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: OPENINGS.map((job, i) => ({
      '@type': 'JobPosting',
      position: i + 1,
      title: job.title,
      employmentType: job.type.toUpperCase().replace('-', '_'),
      hiringOrganization: { '@type': 'Organization', name: 'SHIVKMS' },
      jobLocation: {
        '@type': 'Place',
        address: { '@type': 'PostalAddress', addressLocality: 'Delhi', addressCountry: 'IN' },
      },
      description: job.desc,
    })),
  };

  return (
    <div className="careers-page">
      <SEO
        title="Careers — Join the SHIVKMS Team"
        description="Explore open roles at SHIVKMS. We're hiring full-stack developers, designers, sales and support talent in Delhi and remote. 27+ years building software that matters."
        keywords="SHIVKMS careers, software jobs Delhi, full-stack developer jobs India, tech jobs Delhi NCR"
        path="/careers"
        schema={careersSchema}
      />

      {/* Hero */}
      <section className="careers-hero">
        <div className="careers-hero-bg" />
        <div className="container">
          <div className="careers-hero-inner">
            <span className="eyebrow">We're Hiring</span>
            <h1>Build Software That <span className="gradient-text">Actually Matters</span></h1>
            <p>Join a 27-year-old company still building like a startup — real ownership, real impact, real clients who depend on what you ship.</p>
            <div className="careers-hero-stats">
              <div className="chs-stat"><span className="chs-n">{OPENINGS.length}</span><span className="chs-l">Open Roles</span></div>
              <div className="chs-stat"><span className="chs-n">10+</span><span className="chs-l">Team Members</span></div>
              <div className="chs-stat"><span className="chs-n">27+</span><span className="chs-l">Years Running</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us / Perks */}
      <section className="section-py">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Why SHIVKMS</span>
            <h2>Benefits That <span className="gradient-text">Actually Help</span></h2>
            <div className="divider" />
            <p>We invest in our team the same way we invest in our products — for the long term.</p>
          </div>
          <div className="perks-grid">
            {PERKS.map((p, i) => (
              <div key={i} className="perk-card card">
                <div className="perk-icon">{p.icon}</div>
                <h3 className="perk-title">{p.title}</h3>
                <p className="perk-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="section-py openings-section">
        <div className="openings-bg" />
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">Open Positions</span>
            <h2>Find Your <span className="gradient-text">Next Role</span></h2>
            <div className="divider" />
          </div>

          <div className="dept-filter">
            {DEPARTMENTS.map(d => (
              <button
                key={d}
                className={`dept-tag ${activeDept === d ? 'active' : ''}`}
                onClick={() => setActiveDept(d)}
              >
                {d}
              </button>
            ))}
          </div>

          <div className="jobs-list">
            {filtered.map((job, i) => {
              const isOpen = expandedJob === i;
              return (
                <div key={i} className={`job-card ${isOpen ? 'expanded' : ''}`}>
                  <button className="job-card-header" onClick={() => setExpandedJob(isOpen ? null : i)}>
                    <div className="job-icon">{job.icon}</div>
                    <div className="job-header-text">
                      <h3 className="job-title">{job.title}</h3>
                      <div className="job-meta">
                        <span><MapPin size={13} /> {job.location}</span>
                        <span><Clock size={13} /> {job.type}</span>
                        <span><Briefcase size={13} /> {job.experience}</span>
                      </div>
                    </div>
                    <span className="job-dept-badge">{job.dept}</span>
                    <ChevronDown size={20} className={`job-chevron ${isOpen ? 'rotated' : ''}`} />
                  </button>
                  <div className="job-card-body">
                    <p className="job-desc">{job.desc}</p>
                    <div className="job-skills">
                      {job.skills.map(s => (
                        <span key={s} className="job-skill-chip">{s}</span>
                      ))}
                    </div>
                    <Link
                      to="/contact"
                      className="btn-primary job-apply-btn"
                      state={{ inquiryType: 'Career Opportunity', subject: `Application: ${job.title}` }}
                    >
                      Apply Now <Send size={15} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <p className="no-jobs">No open roles in this department right now — check back soon or send us your resume anyway.</p>
          )}
        </div>
      </section>

      {/* Hiring Process */}
      <section className="section-py process-section">
        <div className="container">
          <div className="section-heading">
            <span className="eyebrow">How We Hire</span>
            <h2>A Process That <span className="gradient-text">Respects Your Time</span></h2>
            <div className="divider" />
          </div>
          <div className="process-steps">
            {PROCESS_STEPS.map((s, i) => (
              <div key={i} className="process-step">
                <span className="process-step-n">{s.n}</span>
                <h3 className="process-step-title">{s.title}</h3>
                <p className="process-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Application CTA */}
      <section className="careers-cta">
        <div className="careers-cta-bg" />
        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h2>Don't see the right role?</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '12px auto 32px', maxWidth: '480px', fontSize: '1.05rem' }}>
            We're always looking for great people. Send us your resume and tell us how you'd like to contribute.
          </p>
          <Link
            to="/contact"
            className="btn-primary"
            state={{ inquiryType: 'Career Opportunity', subject: 'General Application' }}
          >
            Send General Application <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}